// src/components/workspace/AssigneeManager.tsx
import { useState } from 'react';
import {
 Dialog,
 DialogTitle,
 DialogContent,
 Box,
 TextField,
 Button,
 IconButton,
 Avatar,
 List,
 ListItem,
 ListItemAvatar,
 ListItemText,
 ListItemSecondaryAction,
 DialogActions
} from '@mui/material';
import { Edit2, Trash2, User, UserPlus } from 'lucide-react';
import { Assignee } from './types';

interface AssigneeManagerProps {
 open: boolean;
 onClose: () => void;
 assignees: Assignee[];
 onAdd: (assignee: Omit<Assignee, 'id'>) => Promise<any>;
 onUpdate: (id: string, assignee: Partial<Assignee>) => Promise<any>;
 onDelete: (id: string) => Promise<any>;
}

export const AssigneeManager = ({
 open,
 onClose,
 assignees,
 onAdd,
 onUpdate,
 onDelete
}: AssigneeManagerProps) => {
 const [isDialogOpen, setIsDialogOpen] = useState(false);
 const [editingAssignee, setEditingAssignee] = useState<Assignee | null>(null);
 const [formData, setFormData] = useState({
   name: '',
   email: '',
   avatarUrl: ''
 });
 const [isSubmitting, setIsSubmitting] = useState(false);

 const handleOpenDialog = (assignee?: Assignee) => {
   if (assignee) {
     setEditingAssignee(assignee);
     setFormData({
       name: assignee.name,
       email: assignee.email,
       avatarUrl: assignee.avatarUrl || ''
     });
   } else {
     setEditingAssignee(null);
     setFormData({
       name: '',
       email: '',
       avatarUrl: ''
     });
   }
   setIsDialogOpen(true);
 };

 const handleSubmit = async () => {
   if (!formData.name.trim() || !formData.email.trim()) return;

   setIsSubmitting(true);
   try {
     if (editingAssignee) {
       await onUpdate(editingAssignee.id, formData);
     } else {
       await onAdd(formData);
     }
     setIsDialogOpen(false);
     setFormData({
       name: '',
       email: '',
       avatarUrl: ''
     });
   } catch (error) {
     console.error('Error saving assignee:', error);
   } finally {
     setIsSubmitting(false);
   }
 };

 return (
   <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
     <DialogTitle>Manage Team</DialogTitle>
     <DialogContent>
       <Box sx={{ mb: 2 }}>
         <Button
           startIcon={<UserPlus size={16} />}
           onClick={() => handleOpenDialog()}
           variant="outlined"
           size="small"
         >
           Add Team Member
         </Button>
       </Box>

       <List>
         {assignees.map((assignee) => (
           <ListItem key={assignee.id}>
             <ListItemAvatar>
               {assignee.avatarUrl ? (
                 <Avatar src={assignee.avatarUrl} />
               ) : (
                 <Avatar>
                   <User />
                 </Avatar>
               )}
             </ListItemAvatar>
             <ListItemText
               primary={assignee.name}
               secondary={assignee.email}
             />
             <ListItemSecondaryAction>
               <IconButton
                 edge="end"
                 onClick={() => handleOpenDialog(assignee)}
                 sx={{ mr: 1 }}
               >
                 <Edit2 size={16} />
               </IconButton>
               <IconButton
                 edge="end"
                 onClick={() => onDelete(assignee.id)}
               >
                 <Trash2 size={16} />
               </IconButton>
             </ListItemSecondaryAction>
           </ListItem>
         ))}
       </List>

       <Dialog
         open={isDialogOpen}
         onClose={() => setIsDialogOpen(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>
           {editingAssignee ? 'Edit Team Member' : 'Add Team Member'}
         </DialogTitle>
         <DialogContent>
           <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
             <TextField
               fullWidth
               label="Name"
               value={formData.name}
               onChange={(e) => setFormData({ ...formData, name: e.target.value })}
             />
             <TextField
               fullWidth
               label="Email"
               type="email"
               value={formData.email}
               onChange={(e) => setFormData({ ...formData, email: e.target.value })}
             />
             <TextField
               fullWidth
               label="Avatar URL (optional)"
               value={formData.avatarUrl}
               onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
             />
           </Box>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
           <Button
             onClick={handleSubmit}
             variant="contained"
             disabled={isSubmitting || !formData.name.trim() || !formData.email.trim()}
           >
             {isSubmitting
               ? 'Saving...'
               : editingAssignee
               ? 'Save Changes'
               : 'Add Member'}
           </Button>
         </DialogActions>
       </Dialog>
     </DialogContent>
     <DialogActions>
       <Button onClick={onClose}>Close</Button>
     </DialogActions>
   </Dialog>
 );
};